import { App } from 'antd'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
    TbArrowsMove,
    TbCopy,
    TbDownload,
    TbExternalLink,
    TbEye,
    TbFolder,
    TbInfoCircle,
    TbPencil,
    TbRestore,
    TbTrash,
    TbTrashX,
    TbUserPlus,
} from 'react-icons/tb'

import { itemsCount } from '../Helpers/diskFormat'
import { DISK_FILE_PARAM, diskFolderUrl, diskSharedUrl } from '../Helpers/diskRoutes'
import { diskBrowserStore as store } from '../Stores/DiskBrowserStore'
import { diskPropertiesStore } from '../Stores/DiskPropertiesStore'
import { diskShareStore } from '../Stores/DiskShareStore'
import { useDiskFileOpeners } from './useDiskFileOpeners'

import type { DiskNode } from '../Contracts/DiskNode'
import type { DiskActionResult } from '../Stores/DiskBrowserStore'
import type { MenuProps } from 'antd'

type MenuItems = NonNullable<MenuProps['items']>

/**
 * Дії над рядками диска: відкрити, скачати, перейменувати, перемістити,
 * у кошик, відновити, видалити назавжди — і меню рядка за `_permissions`.
 *
 * Тут лише UI-обв'язка (повідомлення, підтвердження, перехід за адресою);
 * самі операції — у `DiskBrowserStore` і Tasks.
 */
export const useDiskActions = () => {
    const { message, modal } = App.useApp()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()

    const report = (result: DiskActionResult | null, done: string): void => {
        if (result === null) return
        if (result.ok) void message.success(done)
        else void message.error(result.error)
    }

    const openFolder = (storageUuid: string, folderUuid: string | null): void => {
        navigate(diskFolderUrl(storageUuid, folderUuid))
    }

    /** Відкрити файл у переглядачі: адреса `?file=` (`BrowserPage` передає її в `FileViewerStore`). */
    const openFile = (node: DiskNode): void => {
        const next = new URLSearchParams(searchParams)
        next.set(DISK_FILE_PARAM, node.uuid)
        setSearchParams(next)
    }

    const closeFile = (): void => {
        const next = new URLSearchParams(searchParams)
        next.delete(DISK_FILE_PARAM)
        setSearchParams(next, { replace: true })
    }

    /**
     * Папку відкриваємо в просторі, якщо він у людини є; інакше — у «Спільних
     * зі мною» (доступ лише на папці чи предку). Усередині спільної лишаємося
     * в ній, щоб крихти йшли від спільної папки.
     */
    const open = (node: DiskNode): void => {
        if (node.deletedAt !== null) return
        if (node.type === 'folder') {
            const storage = store.storageByUuid(node.storage?.uuid)

            if (store.location?.kind === 'shared' || storage === null) navigate(diskSharedUrl(node.uuid))
            else openFolder(storage.uuid, node.uuid === storage.rootFolderUuid ? null : node.uuid)

            return
        }

        openFile(node)
    }

    /**
     * Папка, у якій лежить вузол (пошук, нещодавні, «Спільні зі мною»). Без
     * простору — у «Спільних»: батько доступний, лише якщо він є в обрізаному
     * шляху (`ancestors` від спільної папки), інакше — сам перелік спільних.
     */
    const openParent = (node: DiskNode): void => {
        const storage = store.storageByUuid(node.storage?.uuid)

        if (!storage) {
            const parentVisible = (node.ancestors ?? []).some((ancestor) => ancestor.uuid === node.parentUuid)
            navigate(diskSharedUrl(parentVisible ? node.parentUuid : null))

            return
        }

        openFolder(storage.uuid, node.parentUuid === storage.rootFolderUuid ? null : node.parentUuid)
    }

    /** Модалка «Поділитися»; після змін — перечитати список (значки) і «Доступ» у дровері. */
    const share = (node: Pick<DiskNode, 'uuid' | 'name'>): void => {
        diskShareStore.open({ uuid: node.uuid, name: node.name }, () => {
            void store.refreshVisible()
            diskPropertiesStore.reloadAccess()
        })
    }

    const { download, openInNewTab } = useDiskFileOpeners()

    /**
     * У кошик. Хто має лише доступ (без ролі простору), кошика того простору не
     * бачить і відновити звідти не зможе — тому спершу питаємо, кому це
     * під силу: учасникам простору з роллю «Редактор» і вище.
     */
    const trash = async (nodes: DiskNode[]): Promise<void> => {
        if (nodes.length === 0) return

        const what = nodes.length === 1 ? `«${nodes[0]?.name ?? ''}»` : itemsCount(nodes.length)
        const foreign = nodes.find((node) => node.storage !== null && store.storageByUuid(node.storage.uuid) === null)
        const run = async (): Promise<void> => report(await store.trash(nodes), `${what} — у кошику`)

        if (!foreign?.storage) {
            await run()

            return
        }

        const content =
            foreign.storage.type === 'personal'
                ? 'Елемент потрапить у кошик особистого диска власника. Ви цього кошика не бачите: відновити звідти зможе лише власник.'
                : `Елемент потрапить у кошик простору «${foreign.storage.title}». Ви цього кошика не бачите: відновити звідти зможуть ті, хто має роль «Редактор» чи вище на всьому просторі.`

        modal.confirm({
            title: `Перемістити ${what} у кошик?`,
            content,
            okText: 'У кошик',
            okButtonProps: { danger: true },
            cancelText: 'Скасувати',
            onOk: run,
        })
    }

    const restore = async (nodes: DiskNode[]): Promise<void> => {
        if (nodes.length === 0) return

        report(
            await store.restore(nodes),
            nodes.length === 1 ? 'Відновлено' : `Відновлено: ${itemsCount(nodes.length)}`
        )
    }

    /** Незворотно — тому з підтвердженням. */
    const purge = (nodes: DiskNode[]): void => {
        if (nodes.length === 0) return

        const single = nodes.length === 1 ? nodes[0] : undefined

        modal.confirm({
            title: single ? `Видалити «${single.name}» назавжди?` : `Видалити назавжди ${itemsCount(nodes.length)}?`,
            content:
                single?.type === 'folder'
                    ? 'Папку буде видалено разом з усім вмістом і версіями файлів. Відновити її вже не вийде.'
                    : 'Файли буде видалено разом з усіма версіями. Відновити їх уже не вийде.',
            okText: 'Видалити назавжди',
            okButtonProps: { danger: true },
            cancelText: 'Скасувати',
            onOk: async () => {
                report(await store.purge(nodes), 'Видалено назавжди')
            },
        })
    }

    const drop = async (folderUuid: string): Promise<void> => {
        report(await store.dropOn(folderUuid), 'Переміщено')
    }

    /** Властивості в дровері поверх списку; рядок, чиї властивості показано, — вибраний, як у Google Drive. */
    const showProperties = (node: DiskNode) => {
        store.setSelected([node.uuid])
        diskPropertiesStore.open(node)
    }

    /** Меню «⋯» рядка вмісту: лише дії, на які є права. */
    const nodeMenu = (node: DiskNode): MenuItems => {
        const can = node.permissions
        const items: MenuItems = []

        // «Властивості» — дровер справа, файл не відкривається (54631)
        const properties = {
            key: 'info',
            icon: <TbInfoCircle />,
            label: 'Властивості',
            onClick: () => showProperties(node),
        }

        if (node.type === 'folder') {
            items.push({ key: 'open', icon: <TbFolder />, label: 'Відкрити', onClick: () => open(node) })
            items.push(properties)
        } else {
            items.push({ key: 'view', icon: <TbEye />, label: 'Переглянути', onClick: () => openFile(node) })
            items.push(properties)
            items.push({
                key: 'tab',
                icon: <TbExternalLink />,
                label: 'Відкрити в новій вкладці',
                onClick: () => void openInNewTab(node),
            })
            items.push({ key: 'download', icon: <TbDownload />, label: 'Скачати', onClick: () => void download(node) })
        }

        items.push({
            key: 'share',
            icon: <TbUserPlus />,
            label: can.share ? 'Поділитися…' : 'Хто має доступ',
            onClick: () => share(node),
        })

        if (store.isSearchActive || store.location?.kind === 'recent' || store.isSharedList) {
            items.push({
                key: 'parent',
                icon: <TbFolder />,
                label: 'Показати в папці',
                onClick: () => openParent(node),
            })
        }

        const edit: MenuItems = []

        if (can.update)
            edit.push({
                key: 'rename',
                icon: <TbPencil />,
                label: 'Перейменувати',
                onClick: () => store.openRename(node),
            })
        if (can.update)
            edit.push({
                key: 'move',
                icon: <TbArrowsMove />,
                label: 'Перемістити',
                onClick: () => store.openTransfer('move', [node]),
            })
        if (can.read)
            edit.push({
                key: 'copy',
                icon: <TbCopy />,
                label: 'Копіювати',
                onClick: () => store.openTransfer('copy', [node]),
            })
        if (can.delete)
            edit.push({
                key: 'trash',
                icon: <TbTrash />,
                label: 'У кошик',
                danger: true,
                onClick: () => void trash([node]),
            })

        return edit.length > 0 ? [...items, { type: 'divider' }, ...edit] : items
    }

    /** Меню рядка кошика. */
    const trashMenu = (node: DiskNode): MenuItems => {
        const items: MenuItems = []

        if (node.permissions.delete) {
            items.push({ key: 'restore', icon: <TbRestore />, label: 'Відновити', onClick: () => void restore([node]) })
        }

        if (node.permissions.manage) {
            items.push({
                key: 'purge',
                icon: <TbTrashX />,
                label: 'Видалити назавжди',
                danger: true,
                onClick: () => purge([node]),
            })
        }

        return items
    }

    return {
        open,
        openFile,
        closeFile,
        openFolder,
        openParent,
        share,
        download,
        openInNewTab,
        trash,
        restore,
        purge,
        drop,
        report,
        nodeMenu,
        trashMenu,
        showProperties,
    }
}
